import React from 'react'
import { Check, Star, Shield, MessageSquare } from 'lucide-react'

export default function Pricing() {
  return (
    <section id="pricing" className="py-20 bg-white dark:bg-gray-900 transition-colors duration-300">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            One Simple Plan,
            <span className="gradient-text"> Everything Included</span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            No hidden fees. No complicated tiers. Just one plan with everything you need.
          </p>
        </div>

        {/* Main Pricing Card */}
        <div className="max-w-xl mx-auto mb-12">
          <div className="relative bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-3xl p-8 sm:p-10 shadow-2xl text-white">
            {/* Popular Badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="inline-flex items-center gap-1 bg-yellow-400 text-gray-900 px-4 py-1.5 rounded-full text-sm font-bold shadow-lg">
                <Star className="w-4 h-4 fill-current" />
                All Features Included
              </span>
            </div>

            {/* Price */}
            <div className="text-center mb-8 mt-4">
              <div className="text-6xl sm:text-7xl font-bold mb-2">P***</div>
              <div className="text-cyan-100 text-lg">/month</div>
            </div>

            {/* SMS Credits Add-on */}
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 mb-6 border border-white/30">
              <div className="flex items-center justify-center gap-2 mb-2">
                <MessageSquare className="w-5 h-5" />
                <span className="font-bold text-lg">SMS Credits (Optional)</span>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">₱2,000</div>
                <p className="text-cyan-100 text-sm">1,000 SMS credits + FREE sender name</p>
              </div>
            </div>

            {/* Features */}
            <ul className="space-y-3 mb-8">
              {[
                'Order Management & Tracking',
                'Customer Database & CRM',
                'Automated SMS Reminders*',
                'Inventory Management',
                'Staff & Payroll Tracking',
                'Expense Tracking',
                'Business Analytics & Reports',
                'Email/SMS Campaigns*',
                'Loyalty Program',
                'Mobile App Access',
                'Priority Support'
              ].map((feature, i) => (
                <li key={i} className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-cyan-100 flex-shrink-0" />
                  <span className="text-white">{feature}</span>
                </li>
              ))}
            </ul>

            {/* CTA */}
            <a
              href="https://app.laundrymate.shop"
              className="block w-full text-center py-4 rounded-xl font-bold text-lg bg-white text-cyan-600 hover:bg-gray-100 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200"
            >
              Get Started Now
            </a>

            <p className="text-center text-cyan-100 text-sm mt-4">
              Simple monthly billing. Cancel anytime.
            </p>
            <p className="text-center text-cyan-200/70 text-xs mt-2">
              *SMS features require SMS credits purchase
            </p>
          </div>
        </div>

        {/* Enterprise Option */}
        <div className="text-center mb-12">
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            Multiple shops or need custom features?
          </p>
          <a
            href="https://m.me/946243658562372"
            target="_blank"
            rel="noopener noreferrer"
            className="text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 font-bold underline"
          >
            Contact us for Enterprise pricing
          </a>
        </div>

        {/* Money Back Guarantee */}
        <div className="bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8 text-center border border-cyan-200 dark:border-gray-700">
          <div className="max-w-2xl mx-auto">
            <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              30-Day Money-Back Guarantee
            </h3>
            <p className="text-gray-700 dark:text-gray-300">
              Try LaundryMate risk-free. If you're not satisfied within the first 30 days, we'll refund your money—no questions asked.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
